import { AssistantDrawer } from '@/components/assistant/AssistantDrawer'
import { BeneficiaryCreator } from '@/components/beneficiary/BeneficiaryCreator'
import {
  CommandCenter,
  CommandCenterProvider,
} from '@/components/command-center/CommandCenter'
import { Logo } from '@/components/layouts/Logo'
import { MeMenu } from '@/components/MeMenu'
import { useView } from '@/hooks'
import { isRoute, RouteNames } from '@/routes'
import { RootState } from '@/store'
import { getCurrentOrganization } from '@/store/helpers'
import { Button } from '@repo/ui/button'
import { Card } from '@repo/ui/card'
import { DataProvider } from '@repo/ui/dataProvider'
import { cn } from '@repo/ui/utils'
import { Home } from 'lucide-react'
import { memo, useCallback, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'

export function Navbar({ isShort = false }: { isShort?: boolean }) {
  const view = useView()
  const location = useLocation()
  const pathname = useMemo(() => location.pathname, [location.pathname])
  const isOrganizationsRoute = useMemo(
    () => isRoute(RouteNames.Organizations, pathname),
    [pathname],
  )
  return (
    <Card className="relative flex min-h-fit flex-col py-3" variant="secondary">
      <div className="z-10 flex flex-1 flex-row items-center justify-start gap-2 pr-4">
        <Button
          size="lg"
          className="pl-4 pr-2 lg:pl-6 lg:pr-4"
          variant="link"
          onClick={view.dashboard().navigate}
        >
          <Logo isWhite={false} size={26} />
        </Button>

        {!isShort && (
          <Button
            variant="outline"
            size="icon"
            className={cn(
              'data-[state=open]:bg-primary-100 px-3 ring-offset-inherit focus-visible:ring-0 focus-visible:ring-offset-0',
            )}
            disabled={isOrganizationsRoute}
            onClick={view.dashboard().navigate}
          >
            <Home className="size-5" />
          </Button>
        )}

        {!isShort && <CommandCenterWrapper />}

        <div className="flex flex-1 items-center justify-end gap-2">
          {isShort && <CommandCenterWrapper />}

          <DataProvider
            selectors={[
              (state: RootState) => state.user.me,
              (state: RootState) => state.user.caseWorker,
              getCurrentOrganization,
            ]}
            render={(user, caseWorker, organization) => (
              <MeMenu
                user={user}
                caseWorker={caseWorker}
                organization={organization}
                display="short"
                className="max-w-fit"
              />
            )}
          />

          {isShort && <AssistantDrawer />}
        </div>
      </div>
    </Card>
  )
}

// Memoized CommandCenter to prevent unnecessary re-renders
const MemoizedCommandCenter = memo(CommandCenter)

function CommandCenterWrapper() {
  const [open, setOpen] = useState(false)

  const handleCreateBeneficiary = useCallback(() => {
    setOpen(true)
  }, [])

  return (
    <>
      <CommandCenterProvider>
        <MemoizedCommandCenter onCreateBeneficiary={handleCreateBeneficiary} />
      </CommandCenterProvider>
      {open && <BeneficiaryCreator modalHandler={{ open, setOpen }} />}
    </>
  )
}
